import { ActionIcon, Anchor, Box, CopyButton, Flex, Group, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CenteredLoading } from 'components/CenteredLoading';
import { GroupProgressBar } from 'components/GroupProgressBar/GroupProgressBar';
import { formatBirthday } from 'utils/dates';
import { renderGenderLabel } from 'utils/general';
import { PatientDetailsTabs, shouldDisplayPatientFusdrBadge } from 'pages/Main/Patients/Patients.helpers';
import { usePatient } from 'hooks/api/useUser';
import { BsCheck, BsCopy } from 'react-icons/bs';
import { FusdrBadge } from 'components/FusdrBadge';
import { useShowRestricted } from 'hooks/useShowRestricted';
import { useProfile } from 'hooks/api/useProfile';
import { useAppContext } from 'context/hooks';

export const PatientDetailsHeader = () => {
  const { t } = useTranslation();
  const { patientId, tab } = useParams<{ patientId: string; tab: string }>();
  const { data: patientData } = usePatient({ patientId });
  const { data, isLoading } = useProfile(patientId);
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const { data: patientProfile } = useProfile(patientId ?? '');
  const eicInfo = patientProfile?.customFields?.eicInfo;
  const { filter } = useShowRestricted();

  const renderFusdrBadge = shouldDisplayPatientFusdrBadge({
    eicInfo: eicInfo ? JSON.parse(eicInfo) : '',
    fdaAllowedDeviceModels: currentUser?.fdaAllowedDeviceModels,
    phoneModel: patientProfile?.fibricheckInfo?.device?.model,
  });

  if (!data || isLoading) return <CenteredLoading />;

  return (
    <Flex p={'sm'} my={'sm'} justify={'space-between'} align={'center'} bg={'var(--mantine-primary-color-light)'}>
      <Box data-testid={'header-patient-detail'}>
        <Group gap={'xs'}>
          <Group gap={'xs'}>
            <Text>{`${formatBirthday(data?.birthday, false)} (${renderGenderLabel(data?.gender)})`}</Text>
          </Group>
          <Text size={'md'}>{'|'}</Text>
          <Group gap={'xs'}>
            <Text>{patientData?.email}</Text>
            <CopyButton value={patientData?.email || ''} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? t('copied_label') : t('copy_label')} position={'right'}>
                  <ActionIcon
                    variant={'subtle'}
                    color={copied ? 'var(--mantine-color-turquoise-7)' : 'var(--mantine-color-gray-7)'}
                    onClick={copy}
                  >
                    {copied ? <BsCheck /> : <BsCopy />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Text size={'md'}>{'|'}</Text>
          <Group>
            <Text>{t('patients_header_prescription')}</Text>
            <GroupProgressBar groupList={patientData?.visibleEnlistments} />
          </Group>
        </Group>
      </Box>
      <Group wrap={'nowrap'}>
        {renderFusdrBadge && <FusdrBadge model={patientProfile?.fibricheckInfo?.device?.model} />}
        {tab !== PatientDetailsTabs.Info && (
          <Anchor
            c={'var(--mantine-color-turquoise-7)'}
            onClick={() => navigate(`/patients/${patientId}/details/${PatientDetailsTabs.Info}${filter}`)}
          >
            {t('patients_header_seemore')}
          </Anchor>
        )}
      </Group>
    </Flex>
  );
};
